import type { ColumnsType } from 'antd/es/table'
import React from 'react'
import { Chiclet } from 'sutra/components/Chiclet/Chiclet'

import { useSyndicatedSourceInfo } from 'features/catalog/features/syndicated-detail/hooks/use-syndicated-source-info'
import {
  Container,
  Explainer,
  Header,
  SectionNote,
  SectionTitle,
  StatLabel,
  StatsRow,
  StatTile,
  StatValue,
  StyledTable,
  Title,
} from 'features/catalog/features/syndicated-detail/syndicated-detail.styles'
import { useCatalogService } from 'features/catalog/service'
import type { FocusListOption, Source } from 'features/catalog/types'
import { numberFormatter } from 'utils/number-format'

type Props = {
  item: FocusListOption
  projectId: string
}

const COLUMNS: ColumnsType<Source> = [
  {
    title: 'Source',
    dataIndex: 'title',
    key: 'title',
    render: (title: string, source) =>
      source.source_url ? (
        <a href={source.source_url} target="_blank" rel="noopener noreferrer">
          {title}
        </a>
      ) : (
        title
      ),
  },
  {
    title: 'Item',
    key: 'item',
    render: (_, source) => source.product.name,
  },
  {
    title: 'Reviews (all time)',
    dataIndex: 'reviews',
    key: 'reviews',
    render: (reviews: number) => numberFormatter(reviews),
    sorter: (a, b) => a.reviews - b.reviews,
  },
  {
    title: 'Last updated',
    dataIndex: 'last_update_time',
    key: 'last_update_time',
  },
  {
    title: 'State',
    dataIndex: 'state',
    key: 'state',
  },
]

/**
 * Detail view for a syndicated-source row. A syndicated source (e.g.
 * "Olaplex") isn't a real filterable dimension value — its reviews are
 * distributed across whichever real retailer sites actually carried them —
 * so it deliberately does NOT reuse the generic `CatalogDetail` (that page's
 * charts/summary filter on `source_type`/`source`, which can never match a
 * syndicated name; see `CatalogDetailContent.tsx`). This is a smaller,
 * honest view: the row's own (date-scoped) stats, plus the real sources
 * feeding this syndication bucket, from `/project/sources/group/info`.
 */
const SyndicatedSourceDetail: React.FC<Props> = ({ item, projectId }) => {
  const catalogService = useCatalogService()
  const { data, isLoading } = useSyndicatedSourceInfo(
    catalogService,
    projectId,
    item.value,
  )

  return (
    <Container>
      <Header>
        <Title>{item.value}</Title>
        <Chiclet $tone="gray">Syndicated</Chiclet>
      </Header>
      <Explainer>
        Syndicated reviews can appear on several retailer sites at once; Yogi
        dedupes them and stores each one under a single syndicated source like
        this. The list below shows every real site feeding this bucket.
      </Explainer>
      <StatsRow>
        <StatTile>
          <StatLabel>Records</StatLabel>
          <StatValue>{numberFormatter(item.n_posts)}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel>Sources</StatLabel>
          <StatValue>{numberFormatter(item.product_count ?? 0)}</StatValue>
        </StatTile>
      </StatsRow>
      <SectionTitle>Sources</SectionTitle>
      <SectionNote>
        Per-source review counts below are all-time — the stats above respect
        the selected date range.
      </SectionNote>
      <StyledTable
        dataSource={data?.sources ?? []}
        columns={COLUMNS}
        rowKey="source_uuid"
        size="small"
        loading={isLoading}
        pagination={{ pageSize: 25, showSizeChanger: true }}
      />
    </Container>
  )
}

export default SyndicatedSourceDetail
